import { ReactNode } from 'react';
import styled from 'styled-components';

export const Dialog = ({
  isOpen,
  children,
  label,
  onClose,
  options,
}: {
  isOpen: boolean;
  children?: ReactNode;
  label?: string;
  onClose?: () => void;
  options: {
    text: string;
    onClick: () => void;
    type?: 'primary' | 'secondary' | 'danger';
  }[];
}) => {
  return (
    <Container $isOpen={isOpen}>
      <Backdrop onClick={onClose} />

      <Content className="scroll">
        <Label>
          {label}
          <CloseButton onClick={onClose}>
            <i className="icon-x" />
          </CloseButton>
        </Label>

        {children}

        <Buttons>
          {options.map((option) => (
            <Button
              key={option.text}
              type={option.type || 'secondary'}
              onClick={option.onClick}
            >
              {option.text}
            </Button>
          ))}
        </Buttons>
      </Content>
    </Container>
  );
};

const Container = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  z-index: 3;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  backdrop-filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.5);

  transition: opacity 0.3s;
  ${({ $isOpen }) => !$isOpen && 'opacity: 0; pointer-events: none;'}
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  position: relative;
  box-sizing: border-box;
  min-width: 500px;
  max-width: 90%;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  padding-top: 0;

  font-family: var(--font-1);
  color: var(--main-2);
  font-size: 14px;

  background-color: var(--neutral-2);
  border: 1px solid var(--neutral-3);
  border-radius: var(--radius-2);

  box-shadow: var(--shadow-1);
`;

const Label = styled.div`
  box-sizing: border-box;
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  padding: 20px 0;

  background-color: var(--neutral-2);

  font-family: var(--font-1);
  color: var(--main-1);
  font-size: 18px;
`;

const CloseButton = styled.div`
  position: absolute;
  top: 0;
  right: -20px;
  cursor: pointer;
  padding: 20px;

  font-family: var(--font-1);
  color: var(--main-2);
  font-size: 14px;

  transition: color 0.3s;

  &:hover {
    color: var(--main-1);
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 5px;
  padding-top: 10px;
`;

const Button = styled.div<{ type: 'primary' | 'secondary' | 'danger' }>`
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 5px;
  background-color: var(--neutral-1);
  color: var(--main-2);
  border: 1px solid var(--neutral-3);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--main-1);
  }

  ${({ type }) =>
    type === 'primary' &&
    `
      border: 1px solid var(--primary-2);
      color: var(--primary-1);
      &:hover {
        border: 1px solid var(--primary-1);
        color: var(--primary-1);
      }
    `};

  ${({ type }) =>
    type === 'danger' &&
    `
        border: 1px solid var(--error-2);
        color: var(--error-1);
        &:hover {
          border: 1px solid var(--error-1);
          color: var(--error-1);
        }
      `};

  transition:
    background-color 0.3s,
    color 0.3s,
    border 0.3s;
`;
